
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { User, Calendar as CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import { ChecklistItem } from '@/hooks/useChecklistItems';

interface ChecklistItemControlsProps {
  item: ChecklistItem;
  profiles: any[];
  onUpdate: (updates: Partial<ChecklistItem>) => void;
  isNotePrivate?: boolean;
}

export const ChecklistItemControls = ({ 
  item, 
  profiles, 
  onUpdate, 
  isNotePrivate = false 
}: ChecklistItemControlsProps) => {
  const [showControls, setShowControls] = useState(false);

  return (
    <div className="space-y-2">
      {!showControls && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowControls(true)}
          className="h-6 text-xs text-muted-foreground"
        >
          <User className="h-3 w-3 mr-1" />
          <CalendarIcon className="h-3 w-3 mr-1" />
          Assign & Schedule
        </Button>
      )}

      {showControls && (
        <div className="flex gap-2 items-center flex-wrap">
          {/* Assignee selection - disabled for private notes */}
          {!isNotePrivate && (
            <Select
              value={item.assigned_to || 'unassigned'}
              onValueChange={(value) => 
                onUpdate({ 
                  assigned_to: value === 'unassigned' ? null : value 
                })
              }
            >
              <SelectTrigger className="h-7 text-xs min-w-[120px]">
                <SelectValue placeholder="Assign to..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {profiles.map((profile) => (
                  <SelectItem key={profile.id} value={profile.id}>
                    {profile.full_name || profile.username || 'Unnamed User'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Due date selection */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 text-xs">
                <CalendarIcon className="h-3 w-3 mr-1" />
                {item.due_date ? format(new Date(item.due_date), 'MMM d') : 'Due date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={item.due_date ? new Date(item.due_date) : undefined}
                onSelect={(date) => 
                  onUpdate({ 
                    due_date: date ? format(date, 'yyyy-MM-dd') : null 
                  })
                }
                disabled={(date) => date < new Date('1900-01-01')}
                initialFocus
              />
              {item.due_date && (
                <div className="p-2 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => onUpdate({ due_date: null })}
                  >
                    Clear due date
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>

          {/* Priority selection */}
          <Select
            value={item.priority}
            onValueChange={(value: 'low' | 'medium' | 'high') => 
              onUpdate({ priority: value })
            }
          >
            <SelectTrigger className="h-7 text-xs w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
            </SelectContent>
          </Select>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowControls(false)}
            className="h-7 w-7 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Show assigned user info when assigned */}
      {item.assigned_profile && !showControls && (
        <div className="flex items-center gap-1 text-xs">
          <Avatar className="h-4 w-4">
            <AvatarFallback className="text-xs">
              {item.assigned_profile.full_name?.charAt(0) || 
               item.assigned_profile.username?.charAt(0) || 
               '?'}
            </AvatarFallback>
          </Avatar>
          <span className="text-muted-foreground">
            {item.assigned_profile.full_name || item.assigned_profile.username}
          </span>
        </div>
      )}
    </div>
  );
};
